import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { env } from '../config/environment';
import { 
  AlertCircle, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  Calendar,
  DollarSign,
  User,
  Activity,
  ChevronDown,
  ChevronRight
} from 'lucide-react';

interface WebhookManagementProps {
  userRole?: 'admin' | 'super_admin' | null;
}

interface WebhookEvent {
  id: string;
  provider: string;
  event_name: string;
  event_id: string;
  payload: any;
  processed: boolean;
  processed_at: string | null;
  created_at: string;
  error_message?: string;
  retry_count?: number;
}

interface FailedSubscription {
  user_id: string;
  email: string;
  event_name: string;
  lemonsqueezy_subscription_id: string;
  lemonsqueezy_order_id: string;
  product_name: string;
  amount: number;
  created_at: string;
  error_details: string;
}

export const WebhookManagement: React.FC<WebhookManagementProps> = ({ userRole }) => {
  const [failedEvents, setFailedEvents] = useState<WebhookEvent[]>([]);
  const [failedSubscriptions, setFailedSubscriptions] = useState<FailedSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState<string | null>(null);
  const [expandedEvent, setExpandedEvent] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<'events' | 'subscriptions'>('events');

  useEffect(() => {
    fetchFailedWebhooks();
  }, []);

  const fetchFailedWebhooks = async () => {
    setLoading(true);
    try {
      // Get table prefix based on environment
      const tablePrefix = env.isProduction ? 'P_' : 'D_';
      
      // Fetch failed webhook events
      const { data: webhookData, error: webhookError } = await supabase
        .from(`${tablePrefix}webhook_events`)
        .select('*')
        .eq('processed', false)
        .order('created_at', { ascending: false })
        .limit(50);

      if (webhookError) throw webhookError;
      setFailedEvents(webhookData || []);

      // Fetch failed subscriptions (payment logs without corresponding subscription updates)
      const { data: failedSubs, error: subsError } = await supabase
        .rpc('get_failed_subscriptions');

      if (subsError) throw subsError;
      setFailedSubscriptions(failedSubs || []);
    } catch (error) {
      console.error('Error fetching failed webhooks:', error);
      setErrorMessage('Failed to load webhook data');
    } finally {
      setLoading(false);
    }
  };

  const retryWebhook = async (eventId: string) => {
    setRetrying(eventId);
    setSuccessMessage(null);
    setErrorMessage(null);

    try {
      const { error } = await supabase.functions.invoke('lmsq-webhook-retry', {
        body: { eventId }
      });

      if (error) throw error;

      setSuccessMessage(`Successfully retried webhook event ${eventId}`);
      await fetchFailedWebhooks(); // Refresh the list
    } catch (error: any) {
      console.error('Error retrying webhook:', error);
      setErrorMessage(`Failed to retry webhook: ${error.message}`);
    } finally {
      setRetrying(null);
    }
  };

  const fixSubscription = async (userId: string, subscriptionData: any) => {
    setRetrying(userId);
    setSuccessMessage(null);
    setErrorMessage(null);

    try {
      // Get table prefix based on environment
      const tablePrefix = env.isProduction ? 'P_' : 'D_';
      
      // First check if subscription exists
      const { data: existingSubscription } = await supabase
        .from(`${tablePrefix}user_subscriptions`)
        .select('id')
        .eq('user_id', userId)
        .single();

      if (existingSubscription) {
        console.log('Subscription exists, using RPC function to bypass RLS');
        console.log('Subscription data:', subscriptionData);
        
        // Use RPC function directly to bypass RLS
        const { data: rpcData, error: rpcError } = await supabase.rpc('admin_fix_subscription', {
          p_user_id: userId,
          p_plan_id: subscriptionData.plan_id,
          p_subscription_id: subscriptionData.lemonsqueezy_subscription_id,
          p_order_id: subscriptionData.lemonsqueezy_order_id
        });
        
        if (rpcError) {
          console.error('RPC error:', rpcError);
          throw rpcError;
        }
        
        console.log('RPC result:', rpcData);
        
        if (!rpcData || !rpcData.success) {
          throw new Error(rpcData?.error || 'Failed to update subscription via RPC');
        }
      } else {
        console.log('Inserting new subscription for user:', userId);
        
        // Insert new subscription
        const { data: insertedData, error: insertError } = await supabase
          .from(`${tablePrefix}user_subscriptions`)
          .insert({
            user_id: userId,
            ...subscriptionData,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select()
          .single();

        if (insertError) {
          console.error('Insert error:', insertError);
          throw insertError;
        }
        
        if (!insertedData) {
          console.error('No data returned from insert - possible RLS issue');
          throw new Error('Failed to insert subscription - RLS policy may be blocking the insert');
        }
        
        console.log('Insert successful, returned data:', insertedData);
      }

      setSuccessMessage(`Successfully fixed subscription for user ${userId}`);
      await fetchFailedWebhooks();
    } catch (error: any) {
      console.error('Error fixing subscription:', error);
      setErrorMessage(`Failed to fix subscription: ${error.message}`);
    } finally {
      setRetrying(null);
    }
  };

  const getEventTypeColor = (eventName: string) => {
    if (eventName.includes('created')) return 'text-green-600';
    if (eventName.includes('updated')) return 'text-blue-600';
    if (eventName.includes('cancelled')) return 'text-red-600';
    if (eventName.includes('failed')) return 'text-red-700';
    return 'text-gray-600';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Webhook Management</h2>
        <p className="text-gray-600">Monitor and manage failed webhook events and subscription issues</p>
      </div>

      {/* Alert Messages */}
      {successMessage && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
          <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
          <span className="text-green-800">{successMessage}</span>
        </div>
      )}

      {errorMessage && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
          <XCircle className="h-5 w-5 text-red-600 mr-2" />
          <span className="text-red-800">{errorMessage}</span>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-6 border-b">
        <button
          onClick={() => setSelectedTab('events')}
          className={`px-4 py-2 font-medium transition-colors ${
            selectedTab === 'events'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Failed Events ({failedEvents.length})
        </button>
        <button
          onClick={() => setSelectedTab('subscriptions')}
          className={`px-4 py-2 font-medium transition-colors ${
            selectedTab === 'subscriptions'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Failed Subscriptions ({failedSubscriptions.length})
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <RefreshCw className="h-8 w-8 text-blue-600 animate-spin" />
        </div>
      ) : (
        <>
          {/* Failed Events Tab */}
          {selectedTab === 'events' && (
            <div className="space-y-4">
              {failedEvents.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                  <p>No failed webhook events found</p>
                </div>
              ) : (
                failedEvents.map((event) => (
                  <div key={event.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <button
                            onClick={() => setExpandedEvent(expandedEvent === event.id ? null : event.id)}
                            className="mr-2"
                          >
                            {expandedEvent === event.id ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </button>
                          <span className={`font-semibold ${getEventTypeColor(event.event_name)}`}>
                            {event.event_name}
                          </span>
                          <span className="ml-2 text-sm text-gray-500">
                            #{event.event_id}
                          </span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600 space-x-4">
                          <span className="flex items-center">
                            <Calendar className="h-4 w-4 mr-1" />
                            {formatDate(event.created_at)}
                          </span>
                          <span className="flex items-center">
                            <Activity className="h-4 w-4 mr-1" />
                            Provider: {event.provider}
                          </span>
                          {event.retry_count && event.retry_count > 0 && (
                            <span className="flex items-center text-orange-600">
                              <RefreshCw className="h-4 w-4 mr-1" />
                              Retried: {event.retry_count}x
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => retryWebhook(event.id)}
                        disabled={retrying === event.id}
                        className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                      >
                        {retrying === event.id ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Retry
                          </>
                        )}
                      </button>
                    </div>

                    {/* Expanded Event Details */}
                    {expandedEvent === event.id && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold mb-2">Event Payload:</h4>
                        <pre className="text-xs bg-white p-3 rounded border overflow-x-auto">
                          {JSON.stringify(event.payload, null, 2)}
                        </pre>
                        {event.error_message && (
                          <div className="mt-3">
                            <h4 className="font-semibold text-red-600 mb-1">Error:</h4>
                            <p className="text-sm text-red-700">{event.error_message}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Failed Subscriptions Tab */}
          {selectedTab === 'subscriptions' && (
            <div className="space-y-4">
              {failedSubscriptions.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                  <p>No failed subscriptions found</p>
                </div>
              ) : (
                failedSubscriptions.map((sub) => (
                  <div key={sub.user_id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <User className="h-4 w-4 mr-2 text-gray-600" />
                          <span className="font-semibold">{sub.email}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                          <span>Product: {sub.product_name}</span>
                          <span className="flex items-center">
                            <DollarSign className="h-4 w-4 mr-1" />
                            ${sub.amount}
                          </span>
                          <span>Order: #{sub.lemonsqueezy_order_id}</span>
                          <span>Subscription: #{sub.lemonsqueezy_subscription_id}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600 mt-2">
                          <Calendar className="h-4 w-4 mr-1" />
                          <span>{formatDate(sub.created_at)}</span>
                        </div>
                        <div className="mt-2 text-sm text-red-600">
                          <AlertCircle className="h-4 w-4 inline mr-1" />
                          {sub.error_details || 'Subscription not synced with payment'}
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          if (userRole !== 'super_admin') {
                            alert('super_admin permission required.');
                            return;
                          }
                          const subscriptionData = {
                            plan_id: sub.product_name.toLowerCase().includes('personal') ? 'personal' :
                                    sub.product_name.toLowerCase().includes('business') ? 'business' : 
                                    'enterprise',
                            status: 'active',
                            lemonsqueezy_subscription_id: sub.lemonsqueezy_subscription_id,
                            lemonsqueezy_order_id: sub.lemonsqueezy_order_id,
                            lemonsqueezy_status: 'active',
                            lemonsqueezy_variant_id: sub.product_name.toLowerCase().includes('personal') ? '956046' :
                                                    sub.product_name.toLowerCase().includes('business') ? '956048' : '956049',
                            current_period_start: new Date().toISOString(),
                            current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
                            started_at: new Date().toISOString()
                          };
                          fixSubscription(sub.user_id, subscriptionData);
                        }}
                        disabled={retrying === sub.user_id || userRole !== 'super_admin'}
                        className="ml-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        title={userRole !== 'super_admin' ? 'super_admin permission required' : ''}
                      >
                        {retrying === sub.user_id ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Fix Subscription
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}

      {/* Refresh Button */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={fetchFailedWebhooks}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </button>
      </div>
    </div>
  );
};